import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useFlowObserverPanelStore, FlowObserverPanelEngine, useConsoleLogStore } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ConsoleLogEntry, FetchXhrLogEntry, SocketLogEntry } from '@flow-studio/flow-observer-core';
import { WebJsonField, JsonPrimitive, fmtKey } from '../shared/WebJsonField';
import './ConsoleLogTabContent.css';

const ROW_HEIGHT = 22;

type SubTab = 'all' | 'fetch-xhr' | 'socket';
type DetailTab = 'formatted' | 'raw';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatMs(ms: number | undefined): string {
  if (ms == null) return '';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + (u.search ? u.search.slice(0, 30) : '');
  } catch {
    return url.slice(0, 60);
  }
}

function tryParseJson(s: string | undefined): unknown | null {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
}

function toJsonStr(v: unknown): string | null {
  try { return JSON.stringify(v, null, 2); } catch { return null; }
}

// Extract structured summary fields from a socket entry's payload.
// The payload shape: { messageId, type, data: { item, status, ... }, eventInfo: { eventDescription, ... } }
interface SocketSummary {
  nodeName?: string;
  nodeId?: string;
  stage?: string;
  subStage?: string;
  statusName?: string;
  errorMsg?: string;
}

function extractSocketSummary(entry: SocketLogEntry): SocketSummary {
  const p = entry.payload as any;
  if (!p || typeof p !== 'object') return {};
  const data      = p.data      ?? p.payload?.data      ?? {};
  const eventInfo = p.eventInfo ?? p.payload?.eventInfo ?? {};
  const item      = data.item   ?? {};
  const status    = data.status ?? {};
  const eventDesc = eventInfo.eventDescription ?? {};
  return {
    nodeName:   item.name   != null ? String(item.name)   : undefined,
    nodeId:     item.id     != null ? String(item.id)     : undefined,
    stage:      eventDesc.stage    ?? undefined,
    subStage:   eventDesc.subStage ?? undefined,
    statusName: status.name        ?? undefined,
    errorMsg:   eventInfo.errorMessage ?? data.error?.errorMessage ?? undefined,
  };
}

// ── Small shared atoms ────────────────────────────────────────────────────────

const StatusPill: React.FC<{ status?: number }> = ({ status }) => {
  if (status == null) return null;
  const cls =
    status < 300 ? 'fop-cl-status--ok' :
    status < 400 ? 'fop-cl-status--redirect' :
    'fop-cl-status--err';
  return <span className={`fop-cl-status ${cls}`}>{status}</span>;
};

const DirectionArrow: React.FC<{ dir: 'incoming' | 'outgoing' }> = ({ dir }) => (
  <span className={`fop-cl-dir fop-cl-dir--${dir}`}>{dir === 'incoming' ? '↓' : '↑'}</span>
);

const TypeBadge: React.FC<{ entry: ConsoleLogEntry }> = ({ entry }) => (
  <span className={`fop-cl-typebadge fop-cl-typebadge--${entry._type === 'fetch-xhr' ? 'fetch' : 'ws'}`}>
    {entry._type === 'fetch-xhr' ? (entry.transport ?? 'F').toUpperCase().slice(0, 3) : 'WS'}
  </span>
);

// fmtKey, JsonPrimitive, WebJsonField imported from '../shared/WebJsonField'

// ── Top-level section panel (General / Request Body / Payload …) ──────────────

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="fop-cl-panel">
    <div className="fop-cl-panel__hdr">{title}</div>
    <div className="fop-cl-panel__body">{children}</div>
  </div>
);

const PropRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="wjf-prop">
    <span className="wjf-prop__label">{label}</span>
    <span className="wjf-prop__value">{value}</span>
  </div>
);

const FetchFormatted: React.FC<{ entry: FetchXhrLogEntry }> = ({ entry }) => {
  const reqParsed = tryParseJson(entry.requestBody);
  const resParsed = tryParseJson(entry.responseBody);
  return (
    <div className="fop-cl-fmt">
      <Section title="General">
        <PropRow label="URL"       value={<span className="fop-cl-fmt__url">{entry.url}</span>} />
        <PropRow label="Method"    value={<span className="fop-cl-fmt__method">{entry.method}</span>} />
        <PropRow label="Status"    value={<StatusPill status={entry.status} />} />
        <PropRow label="Transport" value={entry.transport} />
        <PropRow label="Duration"  value={formatMs(entry.durationMs)} />
        <PropRow label="Time"      value={new Date(entry.timestamp).toLocaleTimeString()} />
        {entry.error && <PropRow label="Error" value={<span className="fop-cl-fmt__error">{entry.error}</span>} />}
      </Section>

      {entry.requestBody && (
        <Section title="Request Body">
          {reqParsed != null
            ? <WebJsonField value={reqParsed} />
            : <pre className="fop-cl-fmt__raw">{entry.requestBody}</pre>}
        </Section>
      )}

      {entry.responseBody && (
        <Section title="Response Body">
          {resParsed != null
            ? <WebJsonField value={resParsed} />
            : <pre className="fop-cl-fmt__raw">{entry.responseBody}</pre>}
        </Section>
      )}
    </div>
  );
};

const SocketFormatted: React.FC<{ entry: SocketLogEntry }> = ({ entry }) => {
  const payloadParsed = entry.payload;
  const payloadStr = payloadParsed !== undefined ? toJsonStr(payloadParsed) : null;
  return (
    <div className="fop-cl-fmt">
      <Section title="Event">
        <PropRow label="Direction" value={
          <span className={`fop-cl-fmt__dir fop-cl-fmt__dir--${entry.direction}`}>
            {entry.direction === 'incoming' ? '↓ Incoming' : '↑ Outgoing'}
          </span>
        } />
        <PropRow label="Event"    value={<span className="fop-cl-fmt__event">{entry.event}</span>} />
        {entry.topic   && <PropRow label="Topic"    value={entry.topic} />}
        {entry.subType && <PropRow label="Sub-type" value={<span className="fop-cl-fmt__subtype">{entry.subType}</span>} />}
        <PropRow label="Time" value={new Date(entry.timestamp).toLocaleTimeString()} />
      </Section>

      {payloadParsed !== undefined && (
        <Section title="Payload">
          {typeof payloadParsed === 'object' && payloadParsed !== null
            ? <WebJsonField value={payloadParsed} />
            : <pre className="fop-cl-fmt__raw">{payloadStr ?? String(payloadParsed)}</pre>}
        </Section>
      )}
    </div>
  );
};

// ── Detail panel ──────────────────────────────────────────────────────────────

const EntryDetail: React.FC<{ entry: ConsoleLogEntry; onClose: () => void }> = ({ entry, onClose }) => {
  const [tab, setTab] = useState<DetailTab>('formatted');

  const json = useMemo(() => {
    try { return JSON.stringify(entry, null, 2); } catch { return String(entry); }
  }, [entry]);

  const title = entry._type === 'fetch-xhr'
    ? `${entry.method} ${shortUrl(entry.url)}`
    : `${entry.direction === 'incoming' ? '↓' : '↑'} ${entry.event}`;

  return (
    <div className="fop-cl-detail">
      <div className="fop-cl-detail__header">
        <TypeBadge entry={entry} />
        <span className="fop-cl-detail__title">{title}</span>
        <button className="fop-cl-detail__close" onClick={onClose} aria-label="Close detail">✕</button>
      </div>

      {/* View-mode tab bar */}
      <div className="fop-cl-detail__tabs">
        <button
          className={`fop-cl-dtab ${tab === 'formatted' ? 'fop-cl-dtab--active' : ''}`}
          onClick={() => setTab('formatted')}
        >
          Formatted
        </button>
        <button
          className={`fop-cl-dtab ${tab === 'raw' ? 'fop-cl-dtab--active' : ''}`}
          onClick={() => setTab('raw')}
        >
          Raw JSON
        </button>
      </div>

      {tab === 'raw'
        ? <pre className="fop-cl-detail__body">{json}</pre>
        : entry._type === 'fetch-xhr'
          ? <FetchFormatted entry={entry} />
          : <SocketFormatted entry={entry} />}
    </div>
  );
};

// ── Enable gate ───────────────────────────────────────────────────────────────

const EnableGate: React.FC<{ onEnable: () => void }> = ({ onEnable }) => (
  <div className="fop-cl-gate">
    <svg className="fop-cl-gate__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </svg>
    <p className="fop-cl-gate__msg">Console logging is disabled</p>
    <p className="fop-cl-gate__sub">Captures Fetch/XHR calls and SignalR socket messages in real time.</p>
    <button className="fop-cl-gate__btn" onClick={onEnable}>Enable Console Log</button>
  </div>
);

// ── All list ──────────────────────────────────────────────────────────────────

const AllList: React.FC<{
  entries: ConsoleLogEntry[];
  selected: ConsoleLogEntry | null;
  onSelect: (e: ConsoleLogEntry) => void;
}> = ({ entries, selected, onSelect }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  if (entries.length === 0) return <div className="fop-empty">No activity yet</div>;

  return (
    <div className="fop-cl-scroll" ref={scrollRef}>
      <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map((vi) => {
          const e = entries[vi.index];
          const isSelected = selected?.id === e.id;
          const isErr = e._type === 'fetch-xhr' && (e.phase === 'error' || (e.status != null && e.status >= 400));
          const content = e._type === 'fetch-xhr'
            ? <><span className="fop-cl-method">{e.method}</span><StatusPill status={e.status} /><span className="fop-cl-url" title={e.url}>{shortUrl(e.url)}</span><span className="fop-cl-dur">{formatMs(e.durationMs)}</span></>
            : (() => { const s = extractSocketSummary(e); return <><DirectionArrow dir={e.direction} /><span className="fop-cl-event">{e.event}</span><span className="fop-cl-sk-node">{s.nodeName ?? ''}</span><span className="fop-cl-sk-stage">{s.stage ?? s.statusName ?? ''}</span><span className="fop-cl-sk-substage">{s.subStage ?? ''}</span></>; })();
          return (
            <button
              key={e.id}
              className={[
                'fop-cl-row',
                isSelected ? 'fop-cl-row--selected' : '',
                isErr ? 'fop-cl-row--err' : '',
              ].filter(Boolean).join(' ')}
              style={{ position: 'absolute', top: vi.start, left: 0, width: '100%', height: ROW_HEIGHT }}
              onClick={() => onSelect(e)}
            >
              <span className="fop-cl-time">{new Date(e.timestamp).toLocaleTimeString()}</span>
              <TypeBadge entry={e} />
              {content}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Fetch/XHR list ────────────────────────────────────────────────────────────

const FetchList: React.FC<{
  entries: FetchXhrLogEntry[];
  selected: ConsoleLogEntry | null;
  onSelect: (e: FetchXhrLogEntry) => void;
}> = ({ entries, selected, onSelect }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  if (entries.length === 0) return <div className="fop-empty">No network activity yet</div>;

  return (
    <div className="fop-cl-scroll" ref={scrollRef}>
      <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map((vi) => {
          const e = entries[vi.index];
          const isSelected = selected?._type === 'fetch-xhr' && selected.id === e.id;
          const isErr = e.phase === 'error' || (e.status != null && e.status >= 400);
          return (
            <button
              key={e.id}
              className={[
                'fop-cl-row',
                isSelected ? 'fop-cl-row--selected' : '',
                isErr ? 'fop-cl-row--err' : '',
              ].filter(Boolean).join(' ')}
              style={{ position: 'absolute', top: vi.start, left: 0, width: '100%', height: ROW_HEIGHT }}
              onClick={() => onSelect(e)}
            >
              <span className="fop-cl-time">{new Date(e.timestamp).toLocaleTimeString()}</span>
              <span className="fop-cl-transport">{e.transport}</span>
              <span className="fop-cl-method">{e.method}</span>
              <StatusPill status={e.status} />
              <span className="fop-cl-url" title={e.url}>{shortUrl(e.url)}</span>
              <span className="fop-cl-dur">{formatMs(e.durationMs)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Socket list ───────────────────────────────────────────────────────────────

const SocketList: React.FC<{
  entries: SocketLogEntry[];
  selected: ConsoleLogEntry | null;
  onSelect: (e: SocketLogEntry) => void;
}> = ({ entries, selected, onSelect }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  if (entries.length === 0) return <div className="fop-empty">No socket activity yet</div>;

  return (
    <div className="fop-cl-scroll" ref={scrollRef}>
      <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
        {virtualizer.getVirtualItems().map((vi) => {
          const e = entries[vi.index];
          const isSelected = selected?._type === 'socket' && selected.id === e.id;
          const s = extractSocketSummary(e);
          return (
            <button
              key={e.id}
              className={['fop-cl-row', isSelected ? 'fop-cl-row--selected' : ''].filter(Boolean).join(' ')}
              style={{ position: 'absolute', top: vi.start, left: 0, width: '100%', height: ROW_HEIGHT }}
              onClick={() => onSelect(e)}
            >
              <span className="fop-cl-time">{new Date(e.timestamp).toLocaleTimeString()}</span>
              <DirectionArrow dir={e.direction} />
              <span className="fop-cl-event">{e.event}</span>
              <span className="fop-cl-sk-node" title={s.nodeName}>{s.nodeName ?? ''}</span>
              <span className="fop-cl-sk-id">{s.nodeId ?? ''}</span>
              <span className="fop-cl-sk-stage">{s.stage ?? s.statusName ?? ''}</span>
              <span className="fop-cl-sk-substage">{s.subStage ?? s.errorMsg ?? ''}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export const ConsoleLogTabContent: React.FC<FlowObserverTabContentProps> = ({ tabId }) => {
  const enabled = useConsoleLogStore((s) => s.enabled);
  const setEnabled = useConsoleLogStore((s) => s.setEnabled);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('all');
  const [selected, setSelected] = useState<ConsoleLogEntry | null>(null);

  const dataBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates[tabId]?.dataBuffer ?? []
  ) as ConsoleLogEntry[];

  const fetchEntries = useMemo(
    () => dataBuffer.filter((e): e is FetchXhrLogEntry => e._type === 'fetch-xhr'),
    [dataBuffer]
  );
  const socketEntries = useMemo(
    () => dataBuffer.filter((e): e is SocketLogEntry => e._type === 'socket'),
    [dataBuffer]
  );

  const handleClear = useCallback(() => {
    FlowObserverPanelEngine.clearData(tabId);
    FlowObserverPanelEngine.setTabBadge(tabId, undefined);
    setSelected(null);
  }, [tabId]);

  const handleDisable = useCallback(() => {
    setEnabled(false);
    handleClear();
  }, [setEnabled, handleClear]);

  if (!enabled) {
    return <EnableGate onEnable={() => setEnabled(true)} />;
  }

  const fetchErrCount = fetchEntries.filter((e) => e.phase === 'error' || (e.status != null && e.status >= 400)).length;
  const activeCount =
    activeSubTab === 'all' ? dataBuffer.length :
    activeSubTab === 'fetch-xhr' ? fetchEntries.length :
    socketEntries.length;

  return (
    <div className="fop-cl">
      {/* Sub-tab bar + controls */}
      <div className="fop-cl-toolbar">
        <button
          className={`fop-cl-subtab ${activeSubTab === 'all' ? 'fop-cl-subtab--active' : ''}`}
          onClick={() => setActiveSubTab('all')}
        >
          All
          <span className="fop-cl-subtab__count">{dataBuffer.length}</span>
        </button>
        <button
          className={`fop-cl-subtab ${activeSubTab === 'fetch-xhr' ? 'fop-cl-subtab--active' : ''}`}
          onClick={() => setActiveSubTab('fetch-xhr')}
        >
          Fetch/XHR
          <span className="fop-cl-subtab__count">{fetchEntries.length}</span>
          {fetchErrCount > 0 && <span className="fop-cl-subtab__err">{fetchErrCount}</span>}
        </button>
        <button
          className={`fop-cl-subtab ${activeSubTab === 'socket' ? 'fop-cl-subtab--active' : ''}`}
          onClick={() => setActiveSubTab('socket')}
        >
          Socket
          <span className="fop-cl-subtab__count">{socketEntries.length}</span>
        </button>
        <span className="fop-logs-toolbar__spacer" />
        <span className="fop-cl-recording">
          <span className="fop-cl-recording__dot" />
          Recording
        </span>
        <button className="fop-cl-btn" onClick={handleClear} title="Clear all logs">Clear</button>
        <button className="fop-cl-btn fop-cl-btn--muted" onClick={handleDisable} title="Disable console logging">Disable</button>
      </div>

      {/* List + detail split */}
      <div className="fop-cl-body">
        <div className="fop-cl-list" style={selected ? { flex: '0 0 40%' } : { flex: 1 }}>
          {activeSubTab === 'all' && (
            <>
              {dataBuffer.length > 0 && (
                <div className="fop-cl-header-row">
                  <span className="fop-cl-time">Time</span>
                  <span className="fop-cl-typebadge" style={{ width: 36, flexShrink: 0 }}>Type</span>
                  <span className="fop-cl-url">Name / Event</span>
                </div>
              )}
              <AllList entries={dataBuffer} selected={selected} onSelect={setSelected} />
            </>
          )}

          {activeSubTab === 'fetch-xhr' && (
            <>
              {fetchEntries.length > 0 && (
                <div className="fop-cl-header-row">
                  <span className="fop-cl-time">Time</span>
                  <span className="fop-cl-transport">Via</span>
                  <span className="fop-cl-method">Verb</span>
                  <span className="fop-cl-status">Status</span>
                  <span className="fop-cl-url">URL</span>
                  <span className="fop-cl-dur">ms</span>
                </div>
              )}
              <FetchList entries={fetchEntries} selected={selected} onSelect={setSelected} />
            </>
          )}

          {activeSubTab === 'socket' && (
            <>
              {socketEntries.length > 0 && (
                <div className="fop-cl-header-row">
                  <span className="fop-cl-time">Time</span>
                  <span className="fop-cl-dir">Dir</span>
                  <span className="fop-cl-event">Event</span>
                  <span className="fop-cl-sk-node">Node Name</span>
                  <span className="fop-cl-sk-id">ID</span>
                  <span className="fop-cl-sk-stage">Stage</span>
                  <span className="fop-cl-sk-substage">SubStage</span>
                </div>
              )}
              <SocketList entries={socketEntries} selected={selected} onSelect={setSelected} />
            </>
          )}
        </div>

        {selected && (
          <div className="fop-cl-detail-pane">
            <EntryDetail entry={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>

      {/* Count footer */}
      <div className="fop-cl-footer">
        {activeCount} {activeSubTab === 'fetch-xhr' ? 'requests' : activeSubTab === 'socket' ? 'messages' : 'entries'}
      </div>
    </div>
  );
};
